import type {
  ApplicationGuardrail,
  GuardrailPipeline,
  GuardrailRule,
  GuardrailValidationError,
} from '@terra/shared-contracts-applications';
import { GuardrailPipelineSchema } from '@terra/shared-schemas-orchestrator';

export type GuardrailFormValues = Pick<
  ApplicationGuardrail,
  | 'name'
  | 'description'
  | 'category'
  | 'enabled'
  | 'rules'
  | 'action'
  | 'pipelines'
>;

export type GuardrailFormSource = Omit<GuardrailFormValues, 'pipelines'> & {
  pipelines?: GuardrailPipeline[];
};

export type GuardrailFormErrors = {
  name?: string;
  description?: string;
  rules?: Record<number, string>;
};

export type GuardrailSubmitOutcome =
  | { kind: 'saved' }
  | { kind: 'deferred' }
  | { kind: 'invalid'; validationErrors: GuardrailValidationError[] };

export type GuardrailSaveResponse = {
  success: boolean;
  validationErrors: GuardrailValidationError[] | null;
  message?: string;
};

export type GuardrailFormMessages = {
  nameRequired: string;
  descriptionRequired: string;
  ruleRequired: string;
  maliciousDescription: string;
  maliciousRule: string;
};

export const GUARDRAIL_PIPELINES: readonly GuardrailPipeline[] =
  GuardrailPipelineSchema.options;

const EMPTY_RULE: GuardrailRule = { id: '', rule: '' };

export const EMPTY_GUARDRAIL_FORM_VALUES: GuardrailFormValues = {
  name: '',
  description: '',
  category: 'AUTHENTICATION',
  enabled: true,
  rules: [EMPTY_RULE],
  action: 'WARN',
  pipelines: [],
};

export const toGuardrailFormValues = (
  guardrail: GuardrailFormSource | null,
): GuardrailFormValues =>
  guardrail
    ? {
        name: guardrail.name,
        description: guardrail.description,
        category: guardrail.category,
        enabled: guardrail.enabled,
        rules: guardrail.rules,
        action: guardrail.action,
        pipelines: guardrail.pipelines ?? [],
      }
    : EMPTY_GUARDRAIL_FORM_VALUES;

const withRuleErrors = (
  errors: GuardrailFormErrors,
  ruleErrors: Array<[number, string]>,
): GuardrailFormErrors =>
  ruleErrors.length > 0
    ? { ...errors, rules: Object.fromEntries(ruleErrors) }
    : errors;

export const validateGuardrailForm = ({
  values,
  messages,
}: {
  values: GuardrailFormValues;
  messages: GuardrailFormMessages;
}): GuardrailFormErrors =>
  withRuleErrors(
    {
      ...(values.name.trim() ? {} : { name: messages.nameRequired }),
      ...(values.description?.trim()
        ? {}
        : { description: messages.descriptionRequired }),
    },
    values.rules.flatMap(
      (rule, index): Array<[number, string]> =>
        rule.rule.trim() ? [] : [[index, messages.ruleRequired]],
    ),
  );

export const toGuardrailServerErrors = ({
  validationErrors,
  messages,
}: {
  validationErrors: readonly GuardrailValidationError[];
  messages: GuardrailFormMessages;
}): GuardrailFormErrors => {
  const guardrailViolations = validationErrors.filter(
    (violation) => violation.field === 'guardrail',
  );

  return withRuleErrors(
    guardrailViolations.some(
      (violation) => violation.fieldType === 'description',
    )
      ? { description: messages.maliciousDescription }
      : {},
    guardrailViolations.flatMap(
      (violation): Array<[number, string]> =>
        violation.fieldType === 'rule' &&
        typeof violation.ruleIndex === 'number'
          ? [[violation.ruleIndex, messages.maliciousRule]]
          : [],
    ),
  );
};

export const hasGuardrailFormErrors = (errors: GuardrailFormErrors): boolean =>
  Boolean(errors.name || errors.description || errors.rules);

export const withoutGuardrailRuleError = ({
  errors,
  index,
}: {
  errors: GuardrailFormErrors;
  index: number;
}): GuardrailFormErrors => {
  const remaining = Object.entries(errors.rules ?? {}).filter(
    ([ruleIndex]) => Number(ruleIndex) !== index,
  );

  return {
    ...errors,
    rules: remaining.length > 0 ? Object.fromEntries(remaining) : undefined,
  };
};

export const updateGuardrailRule = ({
  rules,
  index,
  value,
}: {
  rules: readonly GuardrailRule[];
  index: number;
  value: string;
}): GuardrailRule[] =>
  rules.map((rule, ruleIndex) =>
    ruleIndex === index ? { ...rule, rule: value } : rule,
  );

export const removeGuardrailRule = ({
  rules,
  index,
}: {
  rules: readonly GuardrailRule[];
  index: number;
}): GuardrailRule[] => rules.filter((_, ruleIndex) => ruleIndex !== index);

export const appendGuardrailRule = (
  rules: readonly GuardrailRule[],
): GuardrailRule[] => [...rules, EMPTY_RULE];

export const canAppendGuardrailRule = (
  rules: readonly GuardrailRule[],
): boolean => rules.length > 0 && rules.every((rule) => rule.rule.trim());

export const nextGuardrailPipelines = ({
  current,
  next,
  available,
}: {
  current: readonly GuardrailPipeline[];
  next: readonly GuardrailPipeline[];
  available: readonly GuardrailPipeline[];
}): GuardrailPipeline[] => {
  if (next.length === 0) {
    return [...current];
  }

  return next.length === available.length ? [] : [...next];
};

export const toSelectedGuardrailPipelines = ({
  pipelines,
  available,
}: {
  pipelines: readonly GuardrailPipeline[];
  available: readonly GuardrailPipeline[];
}): GuardrailPipeline[] =>
  pipelines.length > 0 ? [...pipelines] : [...available];

export const toGuardrailSubmitOutcome = (
  response: GuardrailSaveResponse,
): GuardrailSubmitOutcome => {
  if (response.success) {
    return { kind: 'saved' };
  }

  if (response.validationErrors?.length) {
    return { kind: 'invalid', validationErrors: response.validationErrors };
  }

  throw new Error(response.message ?? 'Guardrail save was rejected');
};
